import React, { useRef, useState, useCallback } from "react";
import { Box, Chip, Tooltip, useTheme } from "@mui/material";
import { useDrag, useDrop, XYCoord } from "react-dnd";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useDispatch } from "react-redux";
import CustomProgressBar from "global/components/CustomProgressBar/CustomProgressBar";
import { SignRequestStyles } from "../SignRequest.styles";
import { RequestFile } from "models/interfaces";
import { moveFileSignRequest } from "redux/appSlice";
import { useAppSelector } from "hooks/use-app-select";
import { focusRing } from "theme/colors";

export const ItemTypes = { FILE: "file" };

interface DragItem {
  type: string;
  index: number;
}

const DraggableChip = ({
  file,
  index,
  onDelete,
  forceComplete = false,
  dragHandleProps,
}: {
  file: RequestFile;
  index: number;
  onDelete: (index: number) => void;
  forceComplete?: boolean;
  /** react-beautiful-dnd handle props. They go on the grip, not the chip:
   *  the chip is itself a button (it has a delete action), and a handle
   *  around it would nest one control inside another (WCAG 4.1.2). */
  dragHandleProps?: any;
}) => {
  const theme = useTheme();
  const classes = SignRequestStyles(theme);
  const dispatch = useDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const { chipLoader } = useAppSelector((state) => state.app);

  const handleDeleteClick = useCallback(() => {
    onDelete(index);
  }, [index, onDelete]);

  const getShortFileName = useCallback((filename: string) => {
    const baseName = (filename || "").split(".pdf")[0] || "";
    const shortName = baseName.slice(0, 15);
    return baseName.length > 8 ? `${shortName}...` : shortName;
  }, []);

  const fullFileName = file.unsignedPdf || file.unsignedDoc || "";
  const maxShowCharFileName = getShortFileName(fullFileName);

  return (
    <Box
      ref={ref}
      sx={{
        borderRadius: "8px",
        padding: "2px",
        transition: "all 0.2s ease",
        pointerEvents: "auto",
        userSelect: "none",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CustomProgressBar
        uploadIsCompleted={(!file.isUploading || forceComplete) && !chipLoader}
        gradient={theme.palette.primary.light}
      >
        <Box sx={{ position: "relative" }}>
          {/* The drag handle is a sibling laid over the chip's grip, not part of
            the Chip: a Chip with a delete action is itself role="button", so a
            handle inside it would nest one control in another (WCAG 4.1.2). */}
          {dragHandleProps && (
            <Box
              component="span"
              {...dragHandleProps}
              aria-label={
                fullFileName ? `Reorder ${fullFileName}` : "Reorder file"
              }
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "28px",
                zIndex: 1,
                cursor: "grab",
                borderRadius: "8px 0 0 8px",
                "&:focus-visible": {
                  outline: `2px solid ${focusRing}`,
                  outlineOffset: "2px",
                },
              }}
            />
          )}
          <Tooltip
            title={
              maxShowCharFileName.length > 8
                ? file.unsignedPdf || file.unsignedDoc || ""
                : ""
            }
            arrow
          >
            <Chip
              aria-label={fullFileName ? `File: ${fullFileName}` : undefined}
              avatar={
                <DragIndicatorIcon
                  fontSize="inherit"
                  aria-hidden="true"
                  sx={{
                    color: `${theme.palette.text.primary} !important`,
                    cursor: "grab",
                  }}
                />
              }
              label={
                <Box
                  component="span"
                  sx={{
                    color: theme.palette.text.primary,
                    fontSize: "0.75rem",
                  }}
                >
                  {maxShowCharFileName}
                </Box>
              }
              onDelete={handleDeleteClick}
              deleteIcon={
                <CloseIcon
                  role="button"
                  aria-label={
                    fullFileName ? `Remove ${fullFileName}` : "Remove file"
                  }
                  sx={{
                    color: `${theme.palette.text.primary} !important`,
                    fontSize: "14px !important",
                  }}
                />
              }
              sx={{
                ...classes.chip,
                border: `1px solid ${theme.palette.primary.lightest}`,
                pointerEvents: "auto",
                userSelect: "none",
              }}
            />
          </Tooltip>
        </Box>
      </CustomProgressBar>
    </Box>
  );
};

export default DraggableChip;
